import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { IconVideo } from '@tabler/icons-react';
import ClipInterimThumbnail from './ClipInterimThumbnail';
import { Clip } from '@/player-config/src/types/clip-types';
import { getClipThumbnailUrl } from '@/Pages/Sessions/AssetsTable/AssetTableClipThumbnail/ClipPreviewUtils';
import { useAppContext } from '@/context/AppContext/AppContext';
import { getClipBroadcastId } from '@/player-config/src/utils/clipUtils';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import { selectSpeakerDetails, selectTranscript } from '@/player-config/src/selectors/contentMetadataStoreSelectors';
import { getUsedSpeakers } from '@/context/TranscriptContext/TranscriptContextUtils';

const ClipListItemInterimThumbnail = ({ clip }: { clip: Clip }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const { logger } = useAppContext();
  const clipBroadcastId = useMemo(() => getClipBroadcastId(clip), [clip]);
  const transcript = useContentMetadataStore(selectTranscript(clipBroadcastId ?? ''));
  const speakerDetails = useContentMetadataStore(selectSpeakerDetails(clipBroadcastId ?? ''));
  const usedSpeakers = useMemo(() => getUsedSpeakers(speakerDetails), [speakerDetails]);

  const clipThumbnailUrl = getClipThumbnailUrl(clip);
  const clipThumbnailRef = useRef<HTMLImageElement>(null);

  const firstSpeaker = useMemo(() => {
    const firstSpeakerWord = transcript.find(word => {
      return !word.hidden && clip.asset_metadata.start <= word.end_time;
    });
    return usedSpeakers.find(speaker => speaker.key === firstSpeakerWord?.speaker_label);
  }, [clip.asset_metadata.start, transcript, usedSpeakers]);

  const interimThumbnailCaptions = useMemo(() => {
    const srtIndex = transcript.find(
      word => word.start_time >= clip.asset_metadata.start && !word.hidden && word.end_time <= clip.asset_metadata.end
    )?.srt_index;
    return transcript
      .filter(word => {
        return word.srt_index === srtIndex;
      })
      .slice(0, 4);
  }, [clip.asset_metadata.end, clip.asset_metadata.start, transcript]);

  const onImageLoaded = useCallback(() => {
    setImageLoaded(true);
    clipThumbnailRef.current?.setAttribute('currentTime', clip.asset_metadata.start.toString());
  }, [clip.asset_metadata.start]);

  const onImageLoadError = useCallback(() => {
    logger.warn('Clip does not have a thumbnail', { clipId: clip.id });
    setImageError(true);
  }, [clip.id]);

  return (
    <ClipInterimThumbnail
      clip={clip}
      firstSpeaker={firstSpeaker}
      imageLoaded={imageLoaded}
      clipThumbnailRef={clipThumbnailRef}
      captions={interimThumbnailCaptions}
    >
      {!imageError ? (
        <img
          alt="Preview"
          className="size-full object-cover"
          src={clipThumbnailUrl}
          ref={clipThumbnailRef}
          onLoad={onImageLoaded}
          onError={onImageLoadError}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-200/60">
          <IconVideo className="size-5 text-slate-400" />
        </div>
      )}
    </ClipInterimThumbnail>
  );
};

export default memo(ClipListItemInterimThumbnail);
